import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArticleIcon from '@mui/icons-material/Article';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CategoryIcon from '@mui/icons-material/Category';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DifferenceIcon from '@mui/icons-material/Difference';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import InsightsIcon from '@mui/icons-material/Insights';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LabelIcon from '@mui/icons-material/Label';
import LayersIcon from '@mui/icons-material/Layers';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PaidIcon from '@mui/icons-material/Paid';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PublicIcon from '@mui/icons-material/Public';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
// RocketLaunchIcon and ScienceIcon: only used by the hidden PoC/Production Funding Status nav items below.
// import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
// import ScienceIcon from '@mui/icons-material/Science';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import TabIcon from '@mui/icons-material/Tab';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { NavNode } from '@pulse/ui';

// Deliberate exception to "always import MUI from @pulse/ui" — that rule enforces theme/
// variant consistency on components, which icons don't carry. Re-exporting a curated slice
// of @mui/icons-material's 2000+ icons through @pulse/ui would buy nothing. See
// apps/web/CLAUDE.md "Navigation" for the full rationale.
//
// Home sits at the top level with no Domain wrapper. Admin > Accounts is the first real
// Domain > Group > Page nesting — its `permission: 'admin:read'` on the group is filtered by
// filterNavByPermissions() in AppLayout.tsx before this tree reaches AppShell; users without
// admin:read never see the Admin section at all, not just a disabled/empty one.
export const navigation: NavNode[] = [
  {
    kind: 'page',
    id: 'home',
    label: 'Home',
    icon: <HomeIcon fontSize="small" />,
    path: '/home',
  },
  {
    kind: 'page',
    id: 'timesheet',
    label: 'Timesheet',
    icon: <AccessTimeIcon fontSize="small" />,
    path: '/timesheet',
  },
  {
    kind: 'domain',
    id: 'presales',
    label: 'Pre-Sales',
    children: [
      {
        kind: 'page',
        id: 'presales-project-requests',
        label: 'Project Requests',
        icon: <ChecklistIcon fontSize="small" />,
        path: '/pre-sales/project-requests',
        permission: 'presales:read',
      },
      {
        kind: 'page',
        id: 'presales-projects',
        label: 'Projects',
        icon: <FolderIcon fontSize="small" />,
        path: '/pre-sales/projects',
        permission: 'project:read',
      },
    ],
  },
  {
    kind: 'domain',
    id: 'delivery',
    label: 'Delivery',
    children: [
      {
        kind: 'page',
        id: 'delivery-projects',
        label: 'Projects',
        icon: <FolderIcon fontSize="small" />,
        path: '/delivery/projects',
        permission: 'project:read',
      },
      {
        kind: 'page',
        id: 'delivery-timesheet-approvals',
        label: 'Timesheet Approval',
        icon: <AccessTimeIcon fontSize="small" />,
        path: '/delivery/timesheet-approvals',
        permission: 'timesheet:approve',
      },
    ],
  },
  {
    kind: 'domain',
    id: 'approvals',
    label: 'Approvals',
    children: [
      {
        kind: 'page',
        id: 'delivery-exemption-approvals',
        label: 'Funding Exemption Approval',
        icon: <PriceCheckIcon fontSize="small" />,
        path: '/delivery/exemption-approvals',
      },
    ],
  },
  {
    kind: 'domain',
    id: 'accounts-domain',
    label: 'Accounts',
    children: [
      {
        kind: 'page',
        id: 'accounts',
        label: 'All Accounts',
        icon: <BusinessIcon fontSize="small" />,
        path: '/accounts',
        permission: 'account:read',
      },
    ],
  },
  {
    kind: 'domain',
    id: 'finance',
    label: 'Finance',
    children: [
      {
        kind: 'page',
        id: 'finance-people-cost',
        label: 'People Cost',
        icon: <PaidIcon fontSize="small" />,
        path: '/finance/people-cost',
        permission: 'finance:read',
      },
    ],
  },
  {
    kind: 'domain',
    id: 'reporting',
    label: 'Reports',
    children: [
      {
        kind: 'page',
        id: 'reporting-project-insights',
        label: 'Project Insights',
        icon: <InsightsIcon fontSize="small" />,
        path: '/reporting/project-insights',
        permission: 'reporting:read',
      },
      {
        kind: 'group',
        id: 'reporting-timesheet',
        label: 'Timesheet Compliance',
        permission: 'reporting:read',
        children: [
          {
            kind: 'page',
            id: 'reporting-timesheet-compliance',
            label: 'Compliance',
            icon: <FactCheckIcon fontSize="small" />,
            path: '/reporting/timesheet-compliance',
          },
          {
            kind: 'page',
            id: 'reporting-utilization',
            label: 'Utilization',
            icon: <TrendingUpIcon fontSize="small" />,
            path: '/reporting/utilization',
          },
        ],
      },
    ],
  },
  {
    kind: 'domain',
    id: 'admin',
    label: 'Admin',
    children: [
      {
        kind: 'group',
        id: 'admin-accounts',
        label: 'Accounts',
        permission: 'admin:read',
        children: [
          {
            kind: 'page',
            id: 'account-industry-vertical',
            label: 'Industry Vertical',
            icon: <PublicIcon fontSize="small" />,
            path: '/admin/accounts/industry-verticals',
          },
          {
            kind: 'page',
            id: 'account-tier',
            label: 'Account Tier',
            icon: <StarIcon fontSize="small" />,
            path: '/admin/accounts/tiers',
          },
          {
            kind: 'page',
            id: 'account-type',
            label: 'Account Depth',
            icon: <CategoryIcon fontSize="small" />,
            path: '/admin/accounts/types',
          },
          {
            kind: 'page',
            id: 'account-industry-segment',
            label: 'Industry Segment',
            icon: <LayersIcon fontSize="small" />,
            path: '/admin/accounts/industry-segments',
          },
        ],
      },
      {
        kind: 'group',
        id: 'admin-organization',
        label: 'Organization',
        permission: 'admin:read',
        children: [
          {
            kind: 'page',
            id: 'org-departments',
            label: 'Departments',
            icon: <ApartmentIcon fontSize="small" />,
            path: '/admin/organization/departments',
          },
          {
            kind: 'page',
            id: 'org-business-units',
            label: 'Business Units',
            icon: <AccountTreeIcon fontSize="small" />,
            path: '/admin/organization/business-units',
          },
          {
            kind: 'page',
            id: 'org-partners',
            label: 'Partners',
            icon: <HandshakeIcon fontSize="small" />,
            path: '/admin/organization/partners',
          },
          {
            kind: 'page',
            id: 'org-sales-regions',
            label: 'Sales Regions',
            icon: <PublicIcon fontSize="small" />,
            path: '/admin/organization/sales-regions',
          },
          {
            kind: 'page',
            id: 'org-approvers',
            label: 'Approvers',
            icon: <HowToRegIcon fontSize="small" />,
            path: '/admin/organization/approvers',
          },
        ],
      },
      {
        kind: 'group',
        id: 'admin-projects',
        label: 'Projects',
        permission: 'admin:read',
        children: [
          // Hidden from left nav per product request — feature not removed, routes/pages still exist.
          // {
          //   kind: 'page',
          //   id: 'project-poc-funding-status',
          //   label: 'Project PoC Funding Status',
          //   icon: <ScienceIcon fontSize="small" />,
          //   path: '/admin/projects/poc-funding-statuses',
          // },
          // {
          //   kind: 'page',
          //   id: 'project-production-funding-status',
          //   label: 'Project Production Funding Status',
          //   icon: <RocketLaunchIcon fontSize="small" />,
          //   path: '/admin/projects/production-funding-statuses',
          // },
          {
            kind: 'page',
            id: 'project-type',
            label: 'Project Type',
            icon: <DifferenceIcon fontSize="small" />,
            path: '/admin/projects/types',
          },
          {
            kind: 'page',
            id: 'task-category',
            label: 'Task Categories',
            icon: <LabelIcon fontSize="small" />,
            path: '/admin/projects/task-categories',
          },
          {
            kind: 'page',
            id: 'turn-around-time',
            label: 'Turn Around Time (TAT)',
            icon: <TimerIcon fontSize="small" />,
            path: '/admin/projects/turn-around-times',
          },
          {
            kind: 'page',
            id: 'project-funding-type',
            label: 'Project Funding Type',
            icon: <AccountBalanceIcon fontSize="small" />,
            path: '/admin/projects/funding-types',
          },
          {
            kind: 'page',
            id: 'product',
            label: 'Products',
            icon: <Inventory2Icon fontSize="small" />,
            path: '/admin/projects/products',
          },
          {
            kind: 'page',
            id: 'project-construct',
            label: 'Project Construct',
            icon: <BusinessCenterIcon fontSize="small" />,
            path: '/admin/projects/constructs',
          },
          {
            kind: 'page',
            id: 'key-ask',
            label: 'Key Asks',
            icon: <ArticleIcon fontSize="small" />,
            path: '/admin/projects/key-asks',
          },
          {
            kind: 'page',
            id: 'escalation-category',
            label: 'Escalation Categories',
            icon: <ReportProblemIcon fontSize="small" />,
            path: '/admin/projects/escalation-categories',
          },
          {
            kind: 'page',
            id: 'project-tab-defaults',
            label: 'Default Landing Tabs',
            icon: <TabIcon fontSize="small" />,
            path: '/admin/projects/tab-defaults',
          },
        ],
      },
      {
        kind: 'group',
        id: 'admin-common',
        label: 'Common',
        permission: 'admin:read',
        children: [
          {
            kind: 'page',
            id: 'common-currencies',
            label: 'Currencies',
            icon: <AttachMoneyIcon fontSize="small" />,
            path: '/admin/common/currencies',
          },
        ],
      },
      {
        kind: 'group',
        id: 'admin-access-management',
        label: 'Access Management',
        permission: 'admin:read',
        children: [
          {
            kind: 'page',
            id: 'access-roles',
            label: 'Roles',
            icon: <SecurityIcon fontSize="small" />,
            path: '/admin/access-management/roles',
          },
          {
            kind: 'page',
            id: 'access-users',
            label: 'Users',
            icon: <GroupIcon fontSize="small" />,
            path: '/admin/access-management/users',
          },
        ],
      },
      {
        kind: 'page',
        id: 'data-import',
        label: 'Data Import',
        icon: <UploadFileIcon fontSize="small" />,
        path: '/admin/data-import',
        permission: 'admin:read',
      },
      // {
      //   kind: 'group',
      //   id: 'admin-notifications',
      //   label: 'Notifications',
      //   permission: 'admin:read',
      //   children: [
      //     {
      //       kind: 'page',
      //       id: 'notification-schemes',
      //       label: 'Notification Schemes',
      //       icon: <NotificationsActiveIcon fontSize="small" />,
      //       path: '/admin/notifications/schemes',
      //     },
      //   ],
      // },
      {
        kind: 'group',
        id: 'admin-system',
        label: 'System',
        children: [
          {
            kind: 'page',
            id: 'notification-schemes',
            label: 'Notification Schemes',
            icon: <NotificationsActiveIcon fontSize="small" />,
            path: '/admin/notifications/schemes',
            permission: 'admin:read',
          },
          {
            kind: 'page',
            id: 'scheduled-jobs',
            label: 'Scheduled Jobs',
            icon: <EventRepeatIcon fontSize="small" />,
            path: '/admin/scheduled-jobs',
            permission: 'admin:read',
          },
        ],
      },
      {
        kind: 'group',
        id: 'admin-audit',
        label: 'Audit',
        permission: 'admin:read',
        children: [
          {
            kind: 'page',
            id: 'audit-log',
            label: 'Audit Log',
            icon: <HistoryIcon fontSize="small" />,
            path: '/admin/audit-log',
          },
        ],
      },
    ],
  },
];

// Bump alongside apps/web/package.json's version — not build-time-synced, this is a cosmetic
// label shown under the product name in the nav header.
export const APP_VERSION = 'v0.0.1';
